import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useLogin, useMalpediaCheck, useMalpediaComplete, useAuth } from './useAuth';

type LoginMode = 'password' | 'malpedia';
type MalpediaStep = 'token' | 'existing' | 'new';

export default function LoginPage() {
  const navigate = useNavigate();
  const login = useLogin();
  const malpediaCheck = useMalpediaCheck();
  const malpediaComplete = useMalpediaComplete();
  const { data: user } = useAuth();
  const [mode, setMode] = useState<LoginMode>('password');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');

  const [malpediaStep, setMalpediaStep] = useState<MalpediaStep>('token');
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Already authenticated — bounce to home.
  React.useEffect(() => {
    if (user) navigate('/', { replace: true });
  }, [user, navigate]);

  const resetMalpediaStep = () => {
    setMalpediaStep('token');
    setNewUsername('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username || !password) return;
    try {
      await login.mutateAsync({ username, password });
      toast.success('Signed in');
      navigate('/', { replace: true });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Login failed';
      toast.error(msg);
    }
  };

  const handleMalpediaTokenSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    try {
      const result = await malpediaCheck.mutateAsync({ token });
      setMalpediaStep(result.status === 'existing' ? 'existing' : 'new');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Token check failed';
      toast.error(msg);
    }
  };

  const handleMalpediaCompleteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPassword || newPassword !== confirmPassword) return;
    if (malpediaStep === 'new' && !newUsername) return;
    try {
      await malpediaComplete.mutateAsync({
        token,
        password: newPassword,
        ...(malpediaStep === 'new' ? { username: newUsername } : {}),
      });
      toast.success('Signed in');
      navigate('/', { replace: true });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Login failed';
      toast.error(msg);
    }
  };

  return (
    <div className="malpedia-login-page">
      <div className="malpedia-login-card">
        <h1 className="malpedia-login-title">malpediaRAG</h1>
        <p className="malpedia-login-sub">Sign in to continue</p>
        <div className="malpedia-login-mode-toggle" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'password'}
            className={mode === 'password' ? 'is-active' : ''}
            onClick={() => setMode('password')}
          >
            Password
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'malpedia'}
            className={mode === 'malpedia' ? 'is-active' : ''}
            onClick={() => {
              setMode('malpedia');
              resetMalpediaStep();
            }}
          >
            Malpedia token
          </button>
        </div>

        {mode === 'password' ? (
          <form onSubmit={handlePasswordSubmit} className="malpedia-login-form">
            <label className="malpedia-login-field">
              <span>Username</span>
              <input
                type="text"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </label>
            <label className="malpedia-login-field">
              <span>Password</span>
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </label>
            <button
              type="submit"
              className="malpedia-send-btn"
              disabled={login.isPending || !username || !password}
            >
              {login.isPending ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        ) : malpediaStep === 'token' ? (
          <form onSubmit={handleMalpediaTokenSubmit} className="malpedia-login-form">
            <label className="malpedia-login-field">
              <span>Malpedia API token</span>
              <input
                type="password"
                autoComplete="off"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="paste your personal token from Malpedia /settings"
                required
              />
            </label>
            <button
              type="submit"
              className="malpedia-send-btn"
              disabled={malpediaCheck.isPending || !token}
            >
              {malpediaCheck.isPending ? 'Checking…' : 'Continue'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleMalpediaCompleteSubmit} className="malpedia-login-form">
            {malpediaStep === 'existing' ? (
              <p className="malpedia-login-sub">
                This token is already linked to an account — set a new password
                to sign in.
              </p>
            ) : (
              <p className="malpedia-login-sub">
                Token verified — finish creating your account.
              </p>
            )}
            {malpediaStep === 'new' && (
              <label className="malpedia-login-field">
                <span>Username</span>
                <input
                  type="text"
                  autoComplete="username"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  required
                />
              </label>
            )}
            <label className="malpedia-login-field">
              <span>{malpediaStep === 'existing' ? 'New password' : 'Password'}</span>
              <input
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
            </label>
            <label className="malpedia-login-field">
              <span>Confirm password</span>
              <input
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </label>
            {newPassword && confirmPassword && newPassword !== confirmPassword && (
              <p className="malpedia-login-error">Passwords do not match.</p>
            )}
            <button
              type="submit"
              className="malpedia-send-btn"
              disabled={
                malpediaComplete.isPending ||
                !newPassword ||
                newPassword !== confirmPassword ||
                (malpediaStep === 'new' && !newUsername)
              }
            >
              {malpediaComplete.isPending
                ? 'Signing in…'
                : malpediaStep === 'existing'
                  ? 'Set password and sign in'
                  : 'Create account'}
            </button>
            <button
              type="button"
              className="malpedia-login-back"
              onClick={resetMalpediaStep}
            >
              ← Back
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
